// Angular
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { MatDialogConfig } from '@angular/material/dialog';

// Cer
import { CerAppRouteService } from 'src/cer-app/cer-app-route/cer-app-route.service';
import { CerAppChatService } from 'src/cer-app/cer-app-chat/cer-app-chat.service';
import { CerGridComponent } from 'src/cer/cer-grid/cer-grid.component';
import { UiActionTypeEnum, UiCommandEvent, UiCommandSourceEnum } from '@ui/cer-ui-types';
import { CerSessionService } from 'src/cer/cer-session/cer-session.service';
import { CerPromptService } from '@ui/cer-ui-prompt.service';
import { CerMetaUserView } from '@form/cer-form-persistence.service';

// WebApi
import {
  VoucherMessageMailClient,
  VoucherMessageMailSyncCommand,
  VoucherMessageViewClient,
  VoucherViewClient,
  VoucherMessageViewDetailsClient,
  VoucherMessageViewDetailsVm as DetailsVm,
  VoucherMessageViewDetailsVm,
  VoucherMessageViewDto as MessageDto,
  VoucherViewDto as VoucherDto,
  LinkedAttachmentDto,
  ChatDto,
  VoucherMessageStatusAllEnum,
  VoucherMessageChannelTypeEnum,
  VoucherApprovalStatusAllEnum,
  VoucherMessageStatusEnum,
  VoucherApprovalStatusEnum,
  VoucherMessageCreateCommand,
  VoucherViewResponse,
  RoleTypeEnum
} from "../api";

import { VoucherDetailComponent } from './voucher-detail/voucher-detail.component';
import { ActivatedRoute } from '@angular/router';

export class VoucherUpdateResult {
  public ok: boolean;
  public messageDtoOrig?: MessageDto;
  public messageDtoUpdated?: MessageDto;
  public voucherDtoUpdated?: VoucherDto;
  public statusChange?: boolean;
  public attachmentChange?: boolean;
  public editChange?: boolean;
  public chatDto?: ChatDto;
  public nextApprovers?: number[];
  public error?: string;
}

@Injectable(
)

export class VoucherDataService implements OnDestroy {

  // State
  public setupComplete$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public detailsVm$: BehaviorSubject<VoucherMessageViewDetailsVm> = new BehaviorSubject<VoucherMessageViewDetailsVm>(null);
  public detailsVmMap = new Map<number, DetailsVm>();

  // Options
  public filterChannelType: VoucherMessageChannelTypeEnum = VoucherMessageChannelTypeEnum.VendorVoucher;
  public filterMessageStatus: VoucherMessageStatusAllEnum = VoucherMessageStatusAllEnum.All;
  public filterApprovalStatus: VoucherApprovalStatusAllEnum = VoucherApprovalStatusAllEnum.All;
  public filterMyVouchers: boolean = false;
  public filterAllVouchers: boolean = false;
  public filterCustInvoice: boolean = false;
  public filterCustInvoiceDraft: boolean = null;
  public custInvoiceVisible: boolean = false;

  //public userAllVouchers: boolean = false;
  public userIsAdmin: boolean = false;
  public isExpenseVoucher: boolean = false;
  public isVendorVoucher: boolean = false;
  public isManualVoucher: boolean = false;

  constructor(
    private routeService: CerAppRouteService,
    private chatService: CerAppChatService,
    private ui: CerPromptService,
    private session: CerSessionService,
    private voucherMessageMailClient: VoucherMessageMailClient,
    private voucherMessageViewClient: VoucherMessageViewClient,
    private voucherViewClient: VoucherViewClient,
    private messageViewDetailsClient: VoucherMessageViewDetailsClient
  ) {
  }

  public init(activatedRoute: ActivatedRoute) {
    this.routeService.setIsLoading(true);
    this.manage(this.chatService.chats$.subscribe(chat => VoucherDataService.detailsVmMapRefreshChat(this.detailsVmMap, this.chatService, chat)));
    this.manage(activatedRoute.data.subscribe(data => this.setupFromRouteData(activatedRoute.snapshot.url.join('/'),data)));
  }

  private subscriptionManager: Subscription = new Subscription();
  private manage(s: Subscription) {
    this.subscriptionManager.add(s);
  }

  ngOnDestroy() {
    this.subscriptionManager.unsubscribe();
  }

  private routeDataComplete: boolean = false;
  private setupFromRouteData(url: string, routeData: any) {
    if (routeData) {
      this.filterChannelType = routeData?.channelType;
      this.isVendorVoucher = this.filterChannelType == VoucherMessageChannelTypeEnum.VendorVoucher;
      this.isExpenseVoucher = this.filterChannelType == VoucherMessageChannelTypeEnum.ExpenseVoucher;
      this.isManualVoucher = this.filterChannelType == VoucherMessageChannelTypeEnum.ManualVoucher;

      this.filterMessageStatus = routeData['messageStatus'];
      this.filterApprovalStatus = routeData['approvalStatus'];
      this.filterMyVouchers = routeData['myApprovals'];
      this.filterCustInvoice = routeData['custInvoice'];
      this.filterCustInvoiceDraft = (this.filterApprovalStatus != VoucherApprovalStatusAllEnum.Posted && this.filterApprovalStatus != VoucherApprovalStatusAllEnum.All);

      this.custInvoiceVisible = this.isExpenseVoucher;
      this.userIsAdmin = !this.filterMyVouchers;

      var allowAllVouchers = this.filterMyVouchers && this.session.hasUserRoles([RoleTypeEnum.VoucherApprovalAll]);
      if (allowAllVouchers) {
        this.addDesignViewAllowAllVouchers(url);
      }
      this.routeDataComplete = true;
      this.setupComplete();
    }
  }

  private onPersistenceApply(view: CerMetaUserView) {
    if (view?.type == 'design' && view.data) {
      this.filterAllVouchers = view.data.filterAllVouchers === true;
      this.setupComplete();
    }
  }

  private designViewNameAll: string = 'Alle godkendere';
  private addDesignViewAllowAllVouchers(url: string) {
    const nav = this.session.userNavigation;
    nav.viewDesignedAdd(url, this.designViewNameAll, { filterAllVouchers: true });
  }

  public setupComplete() {
    if (this.routeDataComplete) {
      this.setupComplete$.next(true);
      this.routeService.setIsLoading(false);
    }
  }

  // Grid
  public initGrid(grid: CerGridComponent) {
    grid.commmand.subscribe(e => this.onCommand(e));
  }

  // Events
  private onCommand(e: UiCommandEvent): void {
    switch (e.source) {
      case UiCommandSourceEnum.ActionBegin:
        switch (e.actionType) {
          case UiActionTypeEnum.PersistenceApply:
            this.onPersistenceApply(<CerMetaUserView>e.args);

        }
        break;
      case UiCommandSourceEnum.ActionComplete:
        switch (e.actionType) {
          case UiActionTypeEnum.DataBound:
            this.detailsVmReset();
            break;
        }
        break;
    }
  }

  // DetailsVm
  public detailsVmRefresh(messageId: number = null) {
    if (!messageId) {
      messageId = this.detailsVm$.value?.voucherMessageViewDto?.id;
    }
    if (messageId) {
      this.detailsVmMap.delete(messageId);
      this.detailsVmGet(messageId);
    }
  }

  public detailsVmMessageDtoUpdate(messageDto: MessageDto) {
    if (messageDto?.id) {
      var vm = this.detailsVmMap.get(messageDto.id);
      if (vm) {
        vm.voucherMessageViewDto = messageDto;
        this.detailsVm$.next(vm);
      }
    }
  }

  public detailsVmReset() {
    this.detailsVmMap.clear();
    this.detailsVm$.next(null);
  }

  public detailsVmGetFromMap(messageId: number) {
    this.detailsVmGetFromMapPromise(messageId).then();
  }

  public detailsVmGetMapped(messageId: number): DetailsVm {
    if (messageId != null && this.detailsVmMap.has(messageId)) {
      return this.detailsVmMap.get(messageId);
    }
    return null;
  }

  public detailsVmGetFromMapPromise(messageId: number, _this: VoucherDataService = this): Promise<DetailsVm> {
    return new Promise<DetailsVm>((resolve, reject) => {
      if (messageId) {
        if (_this.detailsVmMap.has(messageId)) {
          var vm: DetailsVm = _this.detailsVmMap.get(messageId)
          _this.detailsVm$.next(vm);
          resolve(vm);
        }
        else {
          this.detailsVmGetPromise(messageId, null, _this)
            .then(vm => resolve(vm))
            .catch(error => reject(error));
        }
      }
      else {
        _this.detailsVm$.next(null);
        resolve(null);
      }
    });
  }

  public detailsVmGet(messageId: number) {
    this.detailsVmGetPromise(messageId, null).then();
  }

  public detailsVmGetByVoucherNum(voucherNum: number) {
    this.detailsVmGetPromise(null, voucherNum).then();
  }

  public messageDtoGetByMsgId(messageId: number): Promise<MessageDto> {
    return new Promise<MessageDto>((resolve, reject) => {
      this.detailsVmGetFromMapPromise(messageId).then(vm => resolve(vm?.voucherMessageViewDto));
    });
  }

  public detailsVmGetPromise(messageId: number, voucherNum: number, _this: VoucherDataService = this): Promise<DetailsVm> {
    return new Promise<DetailsVm>((resolve, reject) => {
      if (messageId || voucherNum) {
        (voucherNum ? _this.messageViewDetailsClient.getByVoucherNum(voucherNum)
          : _this.messageViewDetailsClient.get(messageId))
          .subscribe({
            next: detailsVm => {
              messageId = detailsVm?.voucherMessageViewDto?.id;
              if (messageId) {
                _this.detailsVmMap.set(messageId, detailsVm);
                _this.detailsVm$.next(detailsVm);

                //var voucherDto = detailsVm?.voucherViewDtoList?.length > 0 ? detailsVm.voucherViewDtoList[0] : null;
                //console.log('DetailsVm hentet', detailsVm?.voucherMessageViewDto.id, voucherDto?.id, voucherDto?.chat?.length, detailsVm?.chatDtoList?.length);
              }
              else {
                _this.ui.errorToast('Besked ikke fundet', 'Besked ikke fundet');
              }
              resolve(detailsVm);
            },
            error: error => {
              var num: number = messageId ?? voucherNum;
              _this.ui.errorToast('Fejl ved hentning af detaljer ' + num);
              reject('Fejl ved hentning af detaljer ' + num + ' ' + error);
            }
          });
      }
      else {
        reject('Mangler id eller bilagsnr. ved hentning af detaljer');
      }
    });
  }

  // DetailsVM (Attachment)
  public detailsVmAttachmentList(): LinkedAttachmentDto[] {
    var detailsVm: DetailsVm = this.detailsVm$.getValue();
    return detailsVm?.voucherMessageAttachmentDtoList;
  }

  // DetailsVm (Chat)
  public detailsVmMapRefreshFromChat(chatPostedList: ChatDto[]): boolean {
    return VoucherDataService.detailsVmMapRefreshChat(this.detailsVmMap, this.chatService, chatPostedList);
  }

  public static detailsVmMapRefreshChat(detailsVmMap: Map<number, DetailsVm>, chatService: CerAppChatService, chatPostedList: ChatDto[]): boolean {
    var refreshed: boolean = false;
    chatPostedList.forEach(chatPosted => {
      if (chatPosted?.refRowId && chatPosted?.refTableId) {
        if (detailsVmMap.has(chatPosted.refRowId)) {
          var detailsVm: DetailsVm = detailsVmMap.get(chatPosted.refRowId)
          if (VoucherDataService.detailsVmRefreshChat(detailsVm, chatService, chatPosted)) {
            refreshed = true;
          }
        }
      }
    });
    return refreshed;
  }

  public static detailsVmRefreshChats(detailsVm: DetailsVm, chatService: CerAppChatService, chatsPosted: ChatDto[]): boolean {
    var refreshed: boolean = false;
    chatsPosted.forEach(chatPosted => refreshed = refreshed || this.detailsVmRefreshChat(detailsVm, chatService, chatPosted));
    return refreshed;
  }

  public static detailsVmRefreshChat(detailsVm: DetailsVm, chatService: CerAppChatService, chatPosted: ChatDto): boolean {
    var refreshed: boolean = false;
    if (chatPosted.refRowId && chatPosted.refTableId == 1 && detailsVm && detailsVm.voucherMessageViewDto.id && detailsVm.voucherMessageViewDto.id == chatPosted.refRowId) {
      if (chatService.chatToChatsSync(detailsVm.chatDtoList, chatPosted)) {
        refreshed = true;
      }
    }
    return refreshed;
  }

  public voucherDetailsDialogGetConfig(dto: MessageDto,
    attachmentIsActive: boolean, calledFromVoucher: boolean, editPosting: boolean, attachmentSelectedIdxDefault: number,
    jumpToPostingOnVoucherComplete: boolean, showChat: boolean): MatDialogConfig {
    var data: any = {
      dto: dto,
      editVoucher: calledFromVoucher,
      editPosting: editPosting,
      showChat: showChat,
      attachmentsIsActive: attachmentIsActive,
      attachmentSelectedIdxDefault: attachmentSelectedIdxDefault,
      jumpToPostingOnVoucherComplete: jumpToPostingOnVoucherComplete,
      userIsAdmin: this.userIsAdmin,
      detailsVmMap: this.detailsVmMap
    };
    var config = this.ui.matDialogConfigFullScreen(data);
    return config;
  }

  public voucherDetailsOpen(messageDto: MessageDto, config: MatDialogConfig, updateResult: VoucherUpdateResult = null): Promise<VoucherUpdateResult> {
    return new Promise<VoucherUpdateResult>((resolve) => {
      if (!updateResult) {
        updateResult = { ok: false, messageDtoOrig: messageDto };
      }
      updateResult.editChange = true;

      config.data.dto = messageDto;
      this.ui.showDialogComponent(VoucherDetailComponent, config).then(
        result => {
          updateResult.ok = true; //(result.needRefresh === true);
          resolve(updateResult);
        },
        error => {
          updateResult.ok = false;
          updateResult.error = error;
          this.ui.errorToast('Fejl ved dialog visning: ', error);
          resolve(updateResult);
        });
    });
  }

  public apiSetMessageStatus(messageDto: MessageDto, toStatus: VoucherMessageStatusEnum, proformaStatusChange: boolean = false, dialogConfig: MatDialogConfig = null): Promise<VoucherUpdateResult> {
    var _this: VoucherDataService = this;
    return new Promise<VoucherUpdateResult>((resolve, reject) => {
      var updateResult: VoucherUpdateResult = { ok: false, messageDtoOrig: messageDto, statusChange: true };

      if (!messageDto?.id) {
        updateResult.ok = false;
        updateResult.error = 'Ingen meddelelse er valgt';
        reject(updateResult);
      }
      else if (!toStatus || toStatus == messageDto.status) {
        updateResult.ok = false;
        updateResult.error = 'Ingen status ændring angivet';
        reject(updateResult);
      }
      else {
        this.voucherMessageViewClient.setStatus(messageDto.id, toStatus, proformaStatusChange).subscribe({
          next: (messageDtoUpdated: MessageDto) => {
            if (proformaStatusChange) {
              _this.detailsVmGetPromise(messageDto.id, null, _this).then(detailsVm => {
                dialogConfig.data.setMessageStatusVoucherOnClose = true;
                dialogConfig.data.voucherCheckUniqueOnLoad = true;
                dialogConfig.data.dto = messageDtoUpdated;
                _this.voucherDetailsOpen(messageDtoUpdated, dialogConfig, updateResult).then(result => {
                  resolve(updateResult);
                });
              });
            }
            else {
              this.detailsVmMessageDtoUpdate(messageDtoUpdated);
              updateResult.ok = true;
              updateResult.messageDtoUpdated = messageDtoUpdated;
              resolve(updateResult);
            }
          },
          error: (error) => {
            this.ui.errorToast("Kunne ikke opdatere status");
            this.ui.errorApiAction(error).then(action => {
              updateResult.ok = false;
              updateResult.error = error;
              reject(updateResult);
            });
          }
        });
      }
    });
  }

  public apiCreatePostedVoucher(voucherNum: string, transDate: Date, txt: string,
    salesPeriodFrom: Date, salesPeriodTo: Date, salesBOM: boolean
  ): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      var cmd: VoucherMessageCreateCommand = new VoucherMessageCreateCommand();
      cmd.voucherNum = voucherNum;
      cmd.channelType = this.filterChannelType;
      cmd.messageStatus = VoucherMessageStatusEnum.Voucher;
      cmd.voucherStatus = VoucherApprovalStatusEnum.Posted;
      cmd.transDate = transDate;
      cmd.description = txt;
      cmd.salesFromDate = salesPeriodFrom;
      cmd.salesToDate = salesPeriodTo;
      cmd.salesIsBOM = salesBOM;
      this.voucherMessageViewClient.messageCreate(cmd).subscribe({
        next: messageId => {
          this.detailsVmGetFromMap(messageId);
          this.ui.info('Bilaget er oprettet.');
          resolve(messageId);
        },
        error: error => {
          this.ui.errorApi(error);
          reject(0);
        }
      });
    });
  }

  public apiSetApprovalStatus(voucherIdList: number[], approvalStatus: VoucherApprovalStatusEnum): Promise<VoucherUpdateResult> {
    return new Promise<VoucherUpdateResult>((resolve, reject) => {
      var updateResult = new VoucherUpdateResult();
      if (voucherIdList?.length > 0) {
        this.voucherViewClient.setApprovalStatus(approvalStatus, voucherIdList).subscribe({
          next: (voucherViewResponse: VoucherViewResponse) => {
            updateResult.ok = true;
            updateResult.voucherDtoUpdated = voucherViewResponse.voucherViewDto;
            updateResult.chatDto = voucherViewResponse.chatDto;
            resolve(updateResult);
          },
          error: (error) => {
            this.ui.errorApi(error);
            updateResult.ok = false;
            resolve(updateResult);
          }
        });
      }
      else {
        updateResult.ok = false;
        updateResult.error = 'Ingen bilag valgt';
        this.ui.errorToast(updateResult.error);
        reject(updateResult);
      }
    });
  }

  public apiVoucherChannelSync(): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      if (this.userIsAdmin) {
        this.ui.stickyToast('Synkroniserer indbakker');
        this.routeService.setIsLoading(true);
        var cmd = <VoucherMessageMailSyncCommand>{ allVoucherChannels: true, channelType: this.filterChannelType };
        this.voucherMessageMailClient.post(cmd).subscribe({
          next: (result) => {
            this.ui.hideToast();
            this.ui.toast(result == 0 ? 'Ingen nye beskeder hentet' : 'Antal beskeder hentet: ' + result);
            this.routeService.setIsLoading(false);
            resolve(result);
          },
          error: (error) => {
            this.ui.hideToast();
            this.routeService.setIsLoading(false);
            this.ui.errorToast('Kan ikke hente beskeder');
            this.ui.errorApi(error);
            resolve(0);
          }
        });
      }
      else {
        this.ui.errorToast('Bruger har ikke ret til at hente beskeder');
        reject(0);
      }
    });
  }
}
